/**
 * Shared phone number utilities for E.164 formatting.
 */

export const COUNTRY_CODES = [
  { dial: '+1', label: 'US +1' },
  { dial: '+91', label: 'IN +91' },
  { dial: '+44', label: 'UK +44' },
  { dial: '+61', label: 'AU +61' },
  { dial: '+971', label: 'UAE +971' },
  { dial: '+65', label: 'SG +65' },
  { dial: '+49', label: 'DE +49' },
  { dial: '+33', label: 'FR +33' },
  { dial: '+81', label: 'JP +81' },
  { dial: '+86', label: 'CN +86' },
] as const;

export function formatPhoneE164(countryDial: string, localNumber: string): string {
  const cc = countryDial.replace(/\D/g, '');
  let local = localNumber.replace(/\D/g, '');

  if (local.startsWith('0')) {
    local = local.slice(1);
  }

  return `+${cc}${local}`;
}

export function normalizePhoneNumber(phoneNumber: string, defaultCountryCode = '91'): string {
  if (phoneNumber.startsWith('+')) {
    const cleaned = phoneNumber.replace(/\D/g, '');
    return `+${cleaned}`;
  }

  let cleaned = phoneNumber.replace(/\D/g, '');

  if (cleaned.length === 10) {
    cleaned = defaultCountryCode + cleaned;
  }

  return `+${cleaned}`;
}

export function isValidE164(phoneNumber: string): boolean {
  return /^\+[1-9]\d{6,14}$/.test(phoneNumber);
}

/**
 * Every stored spelling of one phone number.
 * Account deletion and signup both use this so `+91…` and a bare 10-digit copy
 * cannot keep the number reserved after the account is gone.
 */
export function phoneLookupKeys(phoneNumber: string | null | undefined): string[] {
  if (!phoneNumber) return [];
  const trimmed = phoneNumber.trim();
  if (!trimmed) return [];

  const keys = new Set<string>();
  keys.add(trimmed);

  const normalized = normalizePhoneNumber(trimmed);
  if (isValidE164(normalized)) keys.add(normalized);

  const digits = trimmed.replace(/\D/g, '');
  if (digits) {
    keys.add(digits);
    keys.add(`+${digits}`);
    if (digits.length > 10) {
      const last10 = digits.slice(-10);
      keys.add(last10);
      keys.add(`+91${last10}`);
      keys.add(`91${last10}`);
    }
  }

  return [...keys].filter((key) => key.length > 0 && key !== '+');
}
